import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {useCallback, useEffect, useRef, useState} from 'react'

type UseIssueSidePanelNavigationProps = {
  sidePanelItemIdentifier: ItemIdentifier | null
  setSidePanelItemIdentifier: (item: ItemIdentifier) => void
}

type UseIssueSidePanelNavigationResult = {
  handleListItemsChange: (items: ItemIdentifier[]) => void
  handleNavigateToNextItem: () => void
  handleNavigateToPreviousItem: () => void
}

/**
 * Custom hook that manages keyboard navigation (j/k) for the issue side panel.
 * Tracks the list of navigable items and provides callbacks for navigating
 * between items. PullRequests are skipped during navigation since they cannot
 * be displayed in the issue side panel.
 */
export function useIssueSidePanelNavigation({
  sidePanelItemIdentifier,
  setSidePanelItemIdentifier,
}: UseIssueSidePanelNavigationProps): UseIssueSidePanelNavigationResult {
  const [listItems, setListItems] = useState<ItemIdentifier[]>([])
  const listItemsRef = useRef<ItemIdentifier[]>([])

  useEffect(() => {
    listItemsRef.current = listItems
  }, [listItems])

  const handleListItemsChange = useCallback((items: ItemIdentifier[]) => {
    setListItems(items)
  }, [])

  const handleNavigateToNextItem = useCallback(() => {
    const items = listItemsRef.current
    if (!sidePanelItemIdentifier || items.length === 0) return

    const currentIndex = items.findIndex(
      item =>
        item.number === sidePanelItemIdentifier.number &&
        item.owner === sidePanelItemIdentifier.owner &&
        item.repo === sidePanelItemIdentifier.repo,
    )
    if (currentIndex < 0 || currentIndex >= items.length - 1) return

    // Find the next Issue, skipping over PullRequests
    for (let i = currentIndex + 1; i < items.length; i++) {
      const nextItem = items[i]
      if (nextItem && nextItem.type === 'Issue') {
        setSidePanelItemIdentifier({
          number: nextItem.number,
          owner: nextItem.owner,
          repo: nextItem.repo,
          type: 'Issue',
        })
        return
      }
    }
  }, [sidePanelItemIdentifier, setSidePanelItemIdentifier])

  const handleNavigateToPreviousItem = useCallback(() => {
    const items = listItemsRef.current
    if (!sidePanelItemIdentifier || items.length === 0) return

    const currentIndex = items.findIndex(
      item =>
        item.number === sidePanelItemIdentifier.number &&
        item.owner === sidePanelItemIdentifier.owner &&
        item.repo === sidePanelItemIdentifier.repo,
    )
    if (currentIndex <= 0) return

    // Find the previous Issue, skipping over PullRequests
    for (let i = currentIndex - 1; i >= 0; i--) {
      const prevItem = items[i]
      if (prevItem && prevItem.type === 'Issue') {
        setSidePanelItemIdentifier({
          number: prevItem.number,
          owner: prevItem.owner,
          repo: prevItem.repo,
          type: 'Issue',
        })
        return
      }
    }
  }, [sidePanelItemIdentifier, setSidePanelItemIdentifier])

  return {
    handleListItemsChange,
    handleNavigateToNextItem,
    handleNavigateToPreviousItem,
  }
}
